import { z } from 'zod';

/**
 * โครงสร้างแบบฟอร์มยืดหยุ่น — สัญญาระหว่าง form builder (admin) → form_template.schema_json
 * → form renderer (LIFF) → visit_answer
 *
 * ดู docs/06-form-schema.md ก่อนแก้ไฟล์นี้ การเพิ่มชนิดฟิลด์ต้องแก้ 4 ที่:
 * types.ts (นี่) · validate.ts · flatten.ts · Renderer ฝั่ง LIFF
 */

export const FIELD_TYPES = [
  'text',
  'textarea',
  'number',
  'currency',
  'date',
  'select',
  'multiselect',
  'boolean',
  'rating',
  'photo',
  'signature',
  'gps',
  'repeater',
  'note',
] as const;

export type FieldType = (typeof FIELD_TYPES)[number];

export const FIELD_TYPE_LABEL_TH: Record<FieldType, string> = {
  text: 'ข้อความสั้น',
  textarea: 'ข้อความยาว',
  number: 'ตัวเลข',
  currency: 'จำนวนเงิน',
  date: 'วันที่',
  select: 'เลือกหนึ่งข้อ',
  multiselect: 'เลือกหลายข้อ',
  boolean: 'ใช่/ไม่ใช่',
  rating: 'ให้คะแนน 1-5',
  photo: 'ภาพถ่าย',
  signature: 'ลายเซ็น',
  gps: 'พิกัด',
  repeater: 'รายการซ้ำได้',
  note: 'ข้อความอธิบาย',
};

/** ฟิลด์ที่ไม่เก็บคำตอบ — renderer แสดงอย่างเดียว */
export const DISPLAY_ONLY_TYPES: FieldType[] = ['note'];

/** ฟิลด์ที่ห้ามอยู่ข้างใน repeater (ซับซ้อนเกินและทำให้ flatten กำกวม) */
export const NOT_ALLOWED_IN_REPEATER: FieldType[] = ['repeater', 'photo', 'signature', 'gps'];

export const FIELD_KEY_RE = /^[a-z][a-z0-9_]{0,49}$/;

/* -------------------------------------------------------------- conditions */

const comparableValue = z.union([z.string(), z.number(), z.boolean(), z.null()]);

export interface LeafCondition {
  field: string;
  eq?: string | number | boolean | null;
  neq?: string | number | boolean | null;
  in?: Array<string | number | boolean>;
  nin?: Array<string | number | boolean>;
  gt?: number;
  gte?: number;
  lt?: number;
  lte?: number;
  truthy?: boolean;
}

export interface GroupCondition {
  all?: Condition[];
  any?: Condition[];
}

export type Condition = LeafCondition | GroupCondition;

const leafConditionSchema = z
  .object({
    field: z.string().min(1),
    eq: comparableValue.optional(),
    neq: comparableValue.optional(),
    in: z.array(z.union([z.string(), z.number(), z.boolean()])).optional(),
    nin: z.array(z.union([z.string(), z.number(), z.boolean()])).optional(),
    gt: z.number().optional(),
    gte: z.number().optional(),
    lt: z.number().optional(),
    lte: z.number().optional(),
    truthy: z.boolean().optional(),
  })
  .strict();

export const conditionSchema: z.ZodType<Condition> = z.lazy(() =>
  z.union([
    leafConditionSchema,
    z.object({ all: z.array(conditionSchema).min(1) }).strict(),
    z.object({ any: z.array(conditionSchema).min(1) }).strict(),
  ]),
);

/* ------------------------------------------------------------------ fields */

export const optionSchema = z
  .object({
    value: z.string().min(1).max(80),
    label: z.string().min(1).max(200),
    /** คะแนนที่บวกเมื่อเลือกตัวเลือกนี้ (ใช้ร่วมกับ scoring_json แบบ map) */
    score: z.number().optional(),
  })
  .strict();

export type FieldOption = z.infer<typeof optionSchema>;

const baseFieldShape = {
  key: z.string().regex(FIELD_KEY_RE, 'key ต้องเป็น a-z, 0-9, _ และขึ้นต้นด้วยตัวอักษร'),
  type: z.enum(FIELD_TYPES),
  label: z.string().min(1).max(300),
  help: z.string().max(500).optional(),
  required: z.boolean().optional(),
  showIf: conditionSchema.optional(),
  /** false = ไม่เขียนลง visit_answer (ข้อความยาว/ภาพ ที่ไม่ใช้ทำรายงาน) */
  reportable: z.boolean().optional(),

  // number / currency / rating / date
  min: z.number().optional(),
  max: z.number().optional(),
  unit: z.string().max(20).optional(),

  // text / textarea
  maxLength: z.number().int().min(1).max(10_000).optional(),
  placeholder: z.string().max(200).optional(),

  // select / multiselect
  options: z.array(optionSchema).max(100).optional(),
  /** ดึงตัวเลือกจากตารางแทนการ hard-code เช่น 'aid_type' */
  optionsFrom: z.enum(['aid_type', 'area', 'staff']).optional(),

  // photo
  gps: z.boolean().optional(),

  default: z.union([z.string(), z.number(), z.boolean(), z.array(z.string())]).optional(),
};

/** ฟิลด์ลูกใน repeater — ไม่มี repeater ซ้อน และไม่มีชนิดที่ต้องเก็บไฟล์ */
export const childFieldSchema = z.object(baseFieldShape).strict();
export type ChildField = z.infer<typeof childFieldSchema>;

export const fieldSchema = z
  .object({
    ...baseFieldShape,
    fields: z.array(childFieldSchema).max(30).optional(), // repeater เท่านั้น
  })
  .strict();

export type FormField = z.infer<typeof fieldSchema>;

/* ---------------------------------------------------------------- sections */

export const sectionSchema = z
  .object({
    key: z.string().regex(FIELD_KEY_RE),
    title: z.string().min(1).max(200),
    description: z.string().max(1000).optional(),
    /** true = ต้องมีความยินยอม sensitive_data ถึงจะแสดง section นี้ */
    sensitive: z.boolean().optional(),
    showIf: conditionSchema.optional(),
    fields: z.array(fieldSchema).min(1).max(100),
  })
  .strict();

export type FormSection = z.infer<typeof sectionSchema>;

export const formSchemaSchema = z
  .object({
    version: z.number().int().min(1).optional(),
    sections: z.array(sectionSchema).min(1).max(30),
  })
  .strict();

export type FormSchema = z.infer<typeof formSchemaSchema>;

/* ----------------------------------------------------------------- scoring */

export const scoringRuleSchema = z
  .object({
    field: z.string().min(1),
    /** ค่าที่เลือก → คะแนน */
    map: z.record(z.string(), z.number()).optional(),
    /** เมื่อค่าเท่ากับ when ให้บวก add */
    when: z.union([z.string(), z.number(), z.boolean()]).optional(),
    add: z.number().optional(),
    /** ช่วงตัวเลข — ใช้กฎแรกที่ตรงแล้วหยุด */
    ranges: z
      .array(
        z
          .object({
            lt: z.number().optional(),
            lte: z.number().optional(),
            gt: z.number().optional(),
            gte: z.number().optional(),
            add: z.number(),
          })
          .strict(),
      )
      .optional(),
    /** สำหรับ repeater: นับแถวที่ตรงเงื่อนไข × multiply (จำกัดที่ max) */
    countWhere: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).optional(),
    multiply: z.number().optional(),
    max: z.number().optional(),
  })
  .strict();

export type ScoringRule = z.infer<typeof scoringRuleSchema>;

export const RISK_LEVELS = ['low', 'medium', 'high', 'critical'] as const;
export type RiskLevelValue = (typeof RISK_LEVELS)[number];

export const scoringSchema = z
  .object({
    base: z.number().default(0),
    rules: z.array(scoringRuleSchema).max(100).default([]),
    /** เรียงจากคะแนนสูงไปต่ำ — ใช้ระดับแรกที่ตรง */
    riskLevel: z
      .array(z.object({ gte: z.number(), level: z.enum(RISK_LEVELS) }).strict())
      .default([]),
  })
  .strict();

export type FormScoring = z.infer<typeof scoringSchema>;

/* ------------------------------------------------------------------ อื่น ๆ */

export const FORM_SCOPES = ['intake', 'followup', 'assessment', 'disbursement'] as const;
export type FormScope = (typeof FORM_SCOPES)[number];

export const FORM_SCOPE_LABEL_TH: Record<FormScope, string> = {
  intake: 'แบบสำรวจแรกรับ',
  followup: 'แบบติดตามผล',
  assessment: 'แบบประเมิน',
  disbursement: 'แบบส่งมอบความช่วยเหลือ',
};

/** ค่าคำตอบที่ renderer ส่งกลับมา */
export type AnswerValue =
  | string
  | number
  | boolean
  | null
  | string[]
  | { lat: number; lng: number; accuracy?: number }
  | Array<Record<string, string | number | boolean | null>>;

export type FormData = Record<string, AnswerValue>;
